import React from 'react';
import { Tooltip } from '@cotera/client/app/components/ui';
import { cn } from '@cotera/client/v0/lib/utils';
import { RESOURCE_LIST_CELL_TEXT } from './resource-list-types';
import { useOrgMembersById } from './use-org-members';
import { MemberAvatar } from './member-avatar';

/**
 * "Created by" cell: the person's avatar plus their name.
 *
 * No prefix text, unlike {@link ResourceOwnerBadge} — the column header already
 * says whose name this is, so repeating it on every row would be noise. The name
 * is spelled out rather than left to a hover, because the whole point of giving
 * attribution its own column is being able to scan down it.
 *
 * Renders nothing when the person can't be resolved: members outside the current
 * org (cross-org external shares) aren't in the roster, and an avatar with no
 * name attached says less than an empty cell.
 */
export const ResourceCreatorCell: React.FC<{
  creatorId: string | undefined;
  /** The viewer, so their own items read as "You" rather than their own name. */
  currentUserId: string;
}> = ({ creatorId, currentUserId }) => {
  const membersById = useOrgMembersById();

  if (creatorId === undefined) {
    return null;
  }

  const member = membersById.get(creatorId);
  const name =
    member !== undefined && member.name !== null && member.name.trim() !== ''
      ? member.name
      : null;
  const email = member?.email ?? null;
  const label = name ?? email;

  if (label === null) {
    return null;
  }

  const isSelf = creatorId === currentUserId;

  return (
    <Tooltip
      side="top"
      asChild
      tooltipContent={
        <div className="flex items-center gap-2 max-w-[240px]">
          <MemberAvatar seed={creatorId} label={label} />
          <div className="flex min-w-0 flex-col">
            <span className="text-xs font-medium text-foreground truncate">
              {label}
            </span>
            {name !== null && email !== null ? (
              <span className="text-xs text-muted-foreground truncate">
                {email}
              </span>
            ) : null}
          </div>
        </div>
      }
    >
      {/* `max-w-full` is what keeps a long name inside its column. This span is
          inline-level, so it takes its width from its content, and the content
          is an avatar next to a nowrap string with nothing to break on — for a
          name like `chris.willis@outseer.com` that comes out wider than the
          140px cell, and the name painted straight over the "Shared with" badge
          beside it. `min-w-0` does not help: it is a floor, not a ceiling. With
          the cap in place the label's `truncate` finally has a box narrow
          enough to ellipsise against. */}
      <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
        <MemberAvatar seed={creatorId} label={label} />
        <span className={cn('truncate', RESOURCE_LIST_CELL_TEXT)}>
          {isSelf ? 'You' : label}
        </span>
      </span>
    </Tooltip>
  );
};
